// src/pages/VerifyCertificate.js
// PUBLIC page — no login required.
import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLang } from '../context/LanguageContext';
import { getPublicCertificateByCertId } from '../firebase/services';

// Analysis key → human readable label
const ANALYSIS_LABELS = {
  en: {
    aspecto:'Appearance',ph:'pH',conductividad:'Conductivity',toc:'TOC',
    nitritos:'Nitrites',nitratos:'Nitrates',color:'Color',turbiedad:'Turbidity',
    cloroLibre:'Free Residual Chlorine',durezaTotal:'Total Hardness',
    sulfatos:'Sulfates',alcalinidad:'Alkalinity / Acidity',metalesPesados:'Heavy Metals',
    densidad:'Density',kFisher:'Karl Fischer (Water)',solubilidad:'Solubility',
    puntoDeFusion:'Melting Point',viscosidad:'Viscosity',
    perdidaPorSecado:'Loss on Drying',indiceRefraccion:'Refractive Index',
    residuoIgnicion:'Ignition Residue',rotacionOptica:'Optical Rotation',
    indiceYodo:'Iodine Value',indiceSaponificacion:'Saponification Value',
    acidez:'Acidity',materiaAnionica:'Anionic Matter Content',
    friabilidad:'Friability',dureza:'Hardness',desintegracion:'Disintegration',
    disolucion:'Dissolution',pesoPromedio:'Average Weight',volumetria:'Volumetry',
    valoracionUV:'UV Assay',valoracionHPLC:'HPLC Assay',otros:'Other Analyses',
  },
  de: {
    aspecto:'Aussehen',ph:'pH',conductividad:'Leitfähigkeit',toc:'TOC',
    nitritos:'Nitrite',nitratos:'Nitrate',color:'Farbe',turbiedad:'Trübung',
    cloroLibre:'Freies Restchlor',durezaTotal:'Gesamthärte',
    sulfatos:'Sulfate',alcalinidad:'Alkalinität / Acidität',metalesPesados:'Schwermetalle',
    densidad:'Dichte',kFisher:'Karl Fischer (Wasser)',solubilidad:'Löslichkeit',
    puntoDeFusion:'Schmelzpunkt',viscosidad:'Viskosität',
    perdidaPorSecado:'Verlust beim Trocknen',indiceRefraccion:'Brechungsindex',
    residuoIgnicion:'Glührückstand',rotacionOptica:'Optische Drehung',
    indiceYodo:'Jodzahl',indiceSaponificacion:'Verseifungszahl',
    acidez:'Säuregehalt',materiaAnionica:'Anionische Materie',
    friabilidad:'Friabilität',dureza:'Härte',desintegracion:'Zerfall',
    disolucion:'Auflösung',pesoPromedio:'Durchschnittsgewicht',volumetria:'Volumetrie',
    valoracionUV:'UV-Valorisierung',valoracionHPLC:'HPLC-Valorisierung',otros:'Andere Analysen',
  },
};

export default function VerifyCertificate() {
  const { certId } = useParams();
  const { lang } = useLang();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    let active = true;
    setLoading(true);
    setNotFound(false);
    getPublicCertificateByCertId(certId)
      .then(result => {
        if (!active) return;
        if (result) setCert(result);
        else setNotFound(true);
      })
      .catch((err) => {
        console.error('VerifyCertificate error:', err?.code, err?.message, err);
        if (active) { setNotFound(true); setErrorMsg(err?.message || err?.code || ''); }
      })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [certId]);

  const tx = {
    en: {
      verifying: 'Verifying certificate...',
      notFoundTitle: 'Certificate Not Found',
      notFoundDesc: "We couldn't find an approved certificate with this ID. It may not exist, may still be under review, or the ID may be incorrect.",
      checkId: 'ID checked:', home: 'Go to COA Lab',
      validTitle: 'Valid Certificate',
      validDesc: 'This certificate has been verified and matches our records.',
      contact: 'Contact Information',
      product: 'Product Information',
      label: '🏷️ Product Label', labelZoom: '🔍 Click to view full size',
      analysisTitle: 'Analysis Requested',
      specsTitle: 'Specifications & Results',
      labTitle: 'Laboratory Information',
      issuedOn: 'Issued on', verifiedBy: 'Approved by',
      chartsTitle: 'Analysis Charts', methodsTitle: 'Methods & Notes',
      purity: 'Purity', totalContent: 'Total Content', expected: 'Expected', fillAccuracy: 'Fill Accuracy',
      poweredBy: 'Certificate verification powered by',
      scanNote: 'This page confirms the authenticity of the Certificate of Analysis. Scan the QR code on any COA Lab certificate to verify it here.',
    },
    de: {
      verifying: 'Zertifikat wird geprüft...',
      notFoundTitle: 'Zertifikat nicht gefunden',
      notFoundDesc: 'Wir konnten kein genehmigtes Zertifikat mit dieser ID finden.',
      checkId: 'Geprüfte ID:', home: 'Zu COA Lab',
      validTitle: 'Gültiges Zertifikat',
      validDesc: 'Dieses Zertifikat wurde verifiziert und entspricht unseren Aufzeichnungen.',
      contact: 'Kontaktinformationen',
      product: 'Produktinformationen',
      label: '🏷️ Produktetikett', labelZoom: '🔍 Klicken zum Vergrößern',
      analysisTitle: 'Angeforderte Analysen',
      specsTitle: 'Spezifikationen & Ergebnisse',
      labTitle: 'Laborinformationen',
      issuedOn: 'Ausgestellt am', verifiedBy: 'Genehmigt von',
      chartsTitle: 'Analysediagramme', methodsTitle: 'Methoden & Notizen',
      purity: 'Reinheit', totalContent: 'Gesamtgehalt', expected: 'Erwartet', fillAccuracy: 'Füllgenauigkeit',
      poweredBy: 'Zertifikatsprüfung bereitgestellt von',
      scanNote: 'Diese Seite bestätigt die Echtheit des Analysenzertifikats.',
    },
  };
  const t = tx[lang] || tx.en;
  const labels = ANALYSIS_LABELS[lang] || ANALYSIS_LABELS.en;

  const formatDate = (ts) => {
    if (!ts) return '—';
    if (ts.seconds) return new Date(ts.seconds * 1000).toLocaleDateString();
    return ts;
  };

  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.centerBox}>
          <div style={s.spinner} />
          <p style={s.loadingText}>{t.verifying}</p>
        </div>
      </div>
    );
  }

  if (notFound) {
    return (
      <div style={s.page}>
        <div style={s.centerBox}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>⚠️</div>
          <h1 style={s.notFoundTitle}>{t.notFoundTitle}</h1>
          <p style={s.notFoundDesc}>{t.notFoundDesc}</p>
          <p style={s.checkedId}>{t.checkId} <span style={s.certIdMono}>{certId}</span></p>
          {errorMsg && <p style={{ fontSize:11, color:'rgba(255,255,255,0.3)', marginBottom:8, fontFamily:'monospace' }}>{errorMsg}</p>}
          <Link to="/" style={s.homeBtn}>{t.home}</Link>
        </div>
      </div>
    );
  }

  // Detect format
  const isNew = !!cert.sample?.productName;
  const sm  = cert.sample  || {};
  const ct  = cert.contact || {};
  const an  = cert.analysis || {};
  const p   = cert.product  || {};
  const lb  = cert.lab      || {};

  return (
    <div style={s.page}>
      <div style={s.wrap}>

        {/* Verified banner */}
        <div style={s.verifiedBanner}>
          <div style={s.verifiedIconWrap}>
            <svg width={28} height={28} viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5">
              <path d="M9 12l2 2 4-4" strokeLinecap="round" strokeLinejoin="round" />
              <circle cx="12" cy="12" r="9" />
            </svg>
          </div>
          <div>
            <div style={s.verifiedTitle}>{t.validTitle}</div>
            <div style={s.verifiedDesc}>{t.validDesc}</div>
          </div>
        </div>

        {/* Certificate card */}
        <div style={s.certCard}>

          {/* Header */}
          <div style={s.coaHeader}>
            <div style={s.coaLogo}>
              <span style={s.logoMain}>COA</span><span style={s.logoAccent}>Lab</span>
            </div>
            <div style={s.coaTitleBlock}>
              <div style={s.coaTitle}>
                {lang === 'de' ? 'ANALYSENZERTIFIKAT' : 'CERTIFICATE OF ANALYSIS'}
              </div>
              <div style={s.coaId}>{cert.certId}</div>
            </div>
          </div>

          {/* ── NEW FORMAT ──────────────────────────────────────────────── */}
          {isNew ? (
            <>
              {/* Contact */}
              {ct.companyName && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.contact}</h3>
                    <div style={s.infoGrid}>
                      {[
                        [lang === 'de' ? 'Unternehmen' : 'Company',       ct.companyName],
                        [lang === 'de' ? 'Adresse' : 'Address',           ct.address],
                        [lang === 'de' ? 'Bestellnr.' : 'Purchase Order', ct.purchaseOrder],
                        [lang === 'de' ? 'Anfragedatum' : 'Request Date', ct.requestDate],
                      ].filter(([,v]) => v).map(([lbl, val]) => (
                        <div key={lbl}>
                          <div style={s.infoLabel}>{lbl}</div>
                          <div style={s.infoValue}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* Product / Sample info */}
              <div style={s.divider} />
              <div style={s.section}>
                <h3 style={s.sectionTitle}>{t.product}</h3>
                <div style={s.infoGrid}>
                  {[
                    [lang === 'de' ? 'Produktname'         : 'Product Name',         sm.productName],
                    [lang === 'de' ? 'Charge / Lot'        : 'Lot / Batch',           sm.lot],
                    [lang === 'de' ? 'Darreichungsform'    : 'Presentation',          sm.presentation],
                    [lang === 'de' ? 'Probenanzahl'        : 'Sample Quantity',       sm.sampleQty],
                    [lang === 'de' ? 'Wirkstoff'           : 'Active Ingredient',     sm.activeIngredient],
                    [lang === 'de' ? 'Dosis/Konzentración' : 'Dose / Concentration',  sm.doseConcentration],
                    [lang === 'de' ? 'Herstellungsdatum'   : 'Manufacturing Date',    sm.mfgDate],
                    [lang === 'de' ? 'Ablaufdatum'         : 'Expiration Date',       sm.expDate],
                    [lang === 'de' ? 'Lieferant'           : 'Supplier',              sm.supplier],
                    [lang === 'de' ? 'Hersteller'          : 'Manufacturer',          sm.manufacturer],
                    [lang === 'de' ? 'Zulassungsnr.'       : 'Sanitary Registry',     sm.sanitaryRegistry],
                    [lang === 'de' ? 'Pharmaform'          : 'Pharmaceutical Form',   sm.pharmaceuticalForm],
                    [lang === 'de' ? 'Produktlinie'        : 'Product Line',          sm.line],
                    [lang === 'de' ? 'Kategorie'           : 'Category',              sm.category],
                    [lang === 'de' ? 'Probentyp'           : 'Sample Type',           Array.isArray(sm.sampleType) ? sm.sampleType.join(', ') : ''],
                    [lang === 'de' ? 'Methodik'            : 'Methodology',           Array.isArray(sm.methodology) ? sm.methodology.join(', ') : ''],
                    [lang === 'de' ? 'Lagerung'            : 'Storage Condition',     sm.storageCondition],
                  ].filter(([,v]) => v && typeof v !== 'object').map(([lbl, val]) => (
                    <div key={lbl}>
                      <div style={s.infoLabel}>{lbl}</div>
                      <div style={s.infoValue}>{String(val)}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product label image */}
              {cert.productLabelUrl && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.label}</h3>
                    <a href={cert.productLabelUrl} target="_blank" rel="noopener noreferrer">
                      <img src={cert.productLabelUrl} alt="Product label" style={s.labelImg} />
                      <div style={s.labelZoomHint}>{t.labelZoom}</div>
                    </a>
                  </div>
                </>
              )}

              {/* Analysis requested */}
              {Object.values(an).some(v => typeof v === 'object' && v?.selected) && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.analysisTitle}</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={s.table}>
                        <thead>
                          <tr style={s.thead}>
                            <th style={s.th}>Parameter</th>
                            <th style={s.th}>{lang === 'de' ? 'Spezifikation' : 'Specification'}</th>
                            <th style={s.th}>{lang === 'de' ? 'Ergebnis' : 'Result'}</th>
                            <th style={s.th}>{lang === 'de' ? 'Bewertung' : 'Assessment'}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(an)
                            .filter(([k,v]) => k !== 'observations' && typeof v === 'object' && v?.selected)
                            .map(([k,v], i) => {
                              const pr   = cert.results?.paramResults?.[k];
                              const res  = pr?.result || '—';
                              // Default to 'pass' if result was entered but pass wasn't explicitly set
                              const pass = pr?.pass ?? (pr?.result ? 'pass' : undefined);
                              return (
                                <tr key={k} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                                  <td style={s.td}>{labels[k] || k}</td>
                                  <td style={s.td}>{String(v.spec || '—')}</td>
                                  <td style={{ ...s.td, fontWeight: 700 }}>{res}</td>
                                  <td style={{ ...s.td, fontWeight: 700,
                                    color: pass === 'pass' ? '#16a34a' : pass === 'fail' ? '#dc2626' : '#888'
                                  }}>
                                    {pass === 'pass' ? '✓ PASS' : pass === 'fail' ? '✕ FAIL' : '—'}
                                  </td>
                                </tr>
                              );
                            })}
                        </tbody>
                      </table>
                    </div>
                    {an.observations && typeof an.observations === 'string' && (
                      <p style={{ marginTop: 12, fontSize: 13, color: '#666', lineHeight: 1.6 }}>
                        <strong>{lang === 'de' ? 'Beobachtungen:' : 'Observations:'}</strong> {an.observations}
                      </p>
                    )}
                  </div>
                </>
              )}
            </>
          ) : (
            /* ── LEGACY FORMAT ─────────────────────────────────────────── */
            <>
              <div style={s.divider} />
              <div style={s.section}>
                <h3 style={s.sectionTitle}>{t.product}</h3>
                <div style={s.infoGrid}>
                  {[
                    [lang === 'de' ? 'Produktname'      : 'Product Name',         p.chemName],
                    ['CAS',                                                         p.casNumber],
                    [lang === 'de' ? 'Molekülformel'    : 'Molecular Formula',     p.formula],
                    [lang === 'de' ? 'Molekulargewicht' : 'Molecular Weight',      p.weight],
                    [lang === 'de' ? 'Chargennummer'    : 'Batch Number',          p.batch],
                    [lang === 'de' ? 'Herstellungsdatum': 'Manufacturing Date',    p.mfgDate],
                    [lang === 'de' ? 'Ablaufdatum'      : 'Expiration Date',       p.expDate],
                    [lang === 'de' ? 'Aussehen'         : 'Appearance',            p.appearance],
                    [lang === 'de' ? 'Herkunftsland'    : 'Country of Origin',     p.country],
                  ].filter(([,v]) => v).map(([lbl, val]) => (
                    <div key={lbl}>
                      <div style={s.infoLabel}>{lbl}</div>
                      <div style={s.infoValue}>{val}</div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Product label (legacy too) */}
              {cert.productLabelUrl && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.label}</h3>
                    <a href={cert.productLabelUrl} target="_blank" rel="noopener noreferrer">
                      <img src={cert.productLabelUrl} alt="Product label" style={s.labelImg} />
                      <div style={s.labelZoomHint}>{t.labelZoom}</div>
                    </a>
                  </div>
                </>
              )}

              {cert.specs?.length > 0 && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.specsTitle}</h3>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={s.table}>
                        <thead>
                          <tr style={s.thead}>
                            {['Parameter','Specification','Result','Unit','Method'].map(h => (
                              <th key={h} style={s.th}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {cert.specs.map((row, i) => (
                            <tr key={i} style={i % 2 === 0 ? s.trEven : s.trOdd}>
                              <td style={s.td}>{row.parameter}</td>
                              <td style={s.td}>{row.specification}</td>
                              <td style={{ ...s.td, fontWeight: 600 }}>{row.result}</td>
                              <td style={s.td}>{row.unit || '—'}</td>
                              <td style={s.td}>{row.method}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}

              {(lb.labName || lb.analyst || lb.approvedBy) && (
                <>
                  <div style={s.divider} />
                  <div style={s.section}>
                    <h3 style={s.sectionTitle}>{t.labTitle}</h3>
                    <div style={s.infoGrid}>
                      {[
                        [lang === 'de' ? 'Labor' : 'Lab',                lb.labName],
                        [lang === 'de' ? 'Analytiker' : 'Analyst',       lb.analyst],
                        [lang === 'de' ? 'Genehmigt von' : 'Approved by',lb.approvedBy],
                        [lang === 'de' ? 'Datum' : 'Date',               lb.approvalDate],
                      ].filter(([,v]) => v).map(([lbl, val]) => (
                        <div key={lbl}>
                          <div style={s.infoLabel}>{lbl}</div>
                          <div style={s.infoValue}>{val}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}
            </>
          )}

          {/* Results highlight bar */}
          {cert.results?.purity && (
            <>
              <div style={s.divider} />
              <div style={s.resultsBar}>
                {[
                  [t.purity,       cert.results.purity + '%'],
                  [t.totalContent, cert.results.totalContent],
                  [t.expected,     cert.results.expectedContent],
                  [t.fillAccuracy, cert.results.fillAccuracy ? cert.results.fillAccuracy + '%' : '—'],
                ].filter(([,v]) => v).map(([lbl, val]) => (
                  <div key={lbl} style={s.resultCell}>
                    <div style={s.resultCellLabel}>{lbl}</div>
                    <div style={s.resultCellVal}>{val}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Charts */}
          {cert.charts?.length > 0 && (
            <>
              <div style={s.divider} />
              <div style={s.section}>
                <h3 style={s.sectionTitle}>{t.chartsTitle}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {cert.charts.map((ch, i) => ch?.url && (
                    <div key={i}>
                      {ch.title && <div style={{ fontSize: 13, fontWeight: 700, color: '#1a1a2e', marginBottom: 6 }}>{ch.title}</div>}
                      <img src={ch.url} alt={ch.title || 'Chart'} style={{ width: '100%', borderRadius: 8, border: '1px solid #e8eaf0' }} />
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* Methods & Notes */}
          {cert.richText?.html && (
            <>
              <div style={s.divider} />
              <div style={s.section}>
                <h3 style={s.sectionTitle}>{t.methodsTitle}</h3>
                <div
                  style={{ fontSize: 13, lineHeight: 1.7, color: '#333' }}
                  dangerouslySetInnerHTML={{ __html: cert.richText.html }}
                />
              </div>
            </>
          )}

          {/* PASS + doctor + dates */}
          <div style={s.divider} />
          <div style={s.bottomSection}>
            <div style={s.resultBlock}>
              <div style={s.passTag}>✓ {lang === 'de' ? 'BESTANDEN' : 'PASS'}</div>
              <p style={s.resultDate}>{t.issuedOn}: {formatDate(cert.createdAt)}</p>
            </div>

            {/* Doctor signature block */}
            {cert.doctorName && (
              <div style={s.doctorBlock}>
                {cert.doctorSignatureUrl && (
                  <img src={cert.doctorSignatureUrl} alt="Doctor signature" style={s.doctorSig} />
                )}
                <div style={s.sigLine} />
                <div style={s.doctorName}>{cert.doctorName}</div>
                {cert.doctorTitle && <div style={s.doctorTitle}>{cert.doctorTitle}</div>}
              </div>
            )}
          </div>

          <div style={s.certFooter}>
            <span>COA Lab</span>
            <span>{cert.certId}</span>
            <span>{formatDate(cert.createdAt)}</span>
          </div>
        </div>

        <p style={s.scanNote}>{t.scanNote}</p>
        <Link to="/" style={s.poweredBy}>{t.poweredBy} <strong>COA Lab</strong></Link>
      </div>
    </div>
  );
}

const s = {
  page:             { minHeight: '100vh', background: '#070F2B', padding: '40px 20px 60px', fontFamily: "'Inter', sans-serif", color: '#fff' },
  wrap:             { maxWidth: 800, margin: '0 auto' },
  centerBox:        { minHeight: '80vh', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 },
  spinner:          { width: 40, height: 40, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#4A9EFF', borderRadius: '50%', animation: 'spin 0.8s linear infinite', marginBottom: 16 },
  loadingText:      { fontSize: 14, color: 'rgba(255,255,255,0.5)' },
  notFoundTitle:    { fontSize: 24, fontWeight: 800, color: '#fff', marginBottom: 10 },
  notFoundDesc:     { fontSize: 14, color: 'rgba(255,255,255,0.55)', maxWidth: 420, lineHeight: 1.6, marginBottom: 16 },
  checkedId:        { fontSize: 13, color: 'rgba(255,255,255,0.4)', marginBottom: 28 },
  certIdMono:       { fontFamily: 'monospace', color: '#4A9EFF' },
  homeBtn:          { background: 'linear-gradient(135deg,#1E5EFF,#0A3DD4)', color: '#fff', textDecoration: 'none', padding: '12px 26px', borderRadius: 12, fontWeight: 700, fontSize: 14 },
  verifiedBanner:   { display: 'flex', alignItems: 'center', gap: 14, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 16, padding: '18px 22px', marginBottom: 20 },
  verifiedIconWrap: { width: 48, height: 48, borderRadius: 24, background: '#22C55E', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  verifiedTitle:    { fontSize: 17, fontWeight: 800, color: '#fff' },
  verifiedDesc:     { fontSize: 13, color: 'rgba(255,255,255,0.6)', marginTop: 2 },
  certCard:         { background: '#fff', borderRadius: 20, overflow: 'hidden', color: '#1a1a2e', boxShadow: '0 24px 80px rgba(0,0,0,0.5)', marginBottom: 24 },
  coaHeader:        { background: '#0A1438', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', gap: 16, flexWrap: 'wrap' },
  coaLogo:          { display: 'flex', alignItems: 'baseline', gap: 3 },
  logoMain:         { fontSize: 22, fontWeight: 900, color: '#fff' },
  logoAccent:       { fontSize: 13, fontWeight: 700, color: '#4A9EFF' },
  coaTitleBlock:    { textAlign: 'right' },
  coaTitle:         { fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: 1.2 },
  coaId:            { fontSize: 11, color: '#4A9EFF', fontFamily: 'monospace', marginTop: 4 },
  divider:          { height: 1, background: '#e8eaf0', margin: '0 28px' },
  section:          { padding: '20px 28px' },
  sectionTitle:     { fontSize: 13, fontWeight: 800, color: '#0A1438', textTransform: 'uppercase', letterSpacing: 0.8, marginBottom: 14 },
  infoGrid:         { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(190px,1fr))', gap: '12px 24px' },
  infoLabel:        { fontSize: 10, color: '#888', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 3 },
  infoValue:        { fontSize: 13, color: '#1a1a2e', fontWeight: 600 },
  labelImg:         { maxWidth: '100%', maxHeight: 300, borderRadius: 10, border: '1px solid #e8eaf0', display: 'block', cursor: 'zoom-in' },
  labelZoomHint:    { fontSize: 12, color: '#888', marginTop: 6 },
  table:            { width: '100%', borderCollapse: 'collapse', minWidth: 400 },
  thead:            { background: '#0A1438' },
  th:               { padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: '#4A9EFF', letterSpacing: 0.4 },
  trEven:           { background: '#f7f8fc' },
  trOdd:            { background: '#fff' },
  td:               { padding: '10px 14px', fontSize: 13, color: '#444', borderBottom: '1px solid #eef0f6' },
  bottomSection:    { display: 'flex', flexWrap: 'wrap', padding: '20px 28px', gap: 12 },
  resultBlock:      {},
  passTag:          { display: 'inline-block', background: '#dcfce7', color: '#16a34a', fontWeight: 800, fontSize: 15, padding: '8px 18px', borderRadius: 20, marginBottom: 10 },
  resultDate:       { fontSize: 12, color: '#888', marginBottom: 2 },
  resultsBar:       { display: 'flex', flexWrap: 'wrap', background: '#0A1438', borderRadius: 0 },
  resultCell:       { flex: 1, minWidth: 120, padding: '14px 20px', textAlign: 'center', borderRight: '1px solid rgba(255,255,255,0.1)' },
  resultCellLabel:  { fontSize: 10, fontWeight: 700, color: 'rgba(130,180,255,0.8)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 },
  resultCellVal:    { fontSize: 20, fontWeight: 900, color: '#fff' },
  doctorBlock:      { flex: 1, padding: '20px 28px', minWidth: 200, display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 4 },
  doctorSig:        { maxWidth: 160, maxHeight: 56, objectFit: 'contain', marginBottom: 4 },
  sigLine:          { width: 160, height: 1, background: '#ccc', marginBottom: 5 },
  doctorName:       { fontSize: 14, fontWeight: 700, color: '#1a1a2e' },
  doctorTitle:      { fontSize: 12, color: '#666' },
  certFooter:       { background: '#0A1438', display: 'flex', justifyContent: 'space-between', padding: '10px 28px', fontSize: 11, color: 'rgba(255,255,255,0.5)' },
  scanNote:         { textAlign: 'center', fontSize: 13, color: 'rgba(255,255,255,0.4)', lineHeight: 1.6, maxWidth: 480, margin: '0 auto 16px' },
  poweredBy:        { display: 'block', textAlign: 'center', fontSize: 13, color: '#4A9EFF', textDecoration: 'none' },
};

if (typeof document !== 'undefined' && !document.getElementById('verify-spin-kf')) {
  const st = document.createElement('style');
  st.id = 'verify-spin-kf';
  st.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(st);
}
